import { toast } from 'sonner';

import { ApiError } from '../lib/api';

/**
 * Notification policy, in one place.
 *
 * A toast is for an outcome the user cannot see for themselves. Saving a chart
 * while looking at the chart needs no toast — the title bar already says
 * "Saved". A background delete, a copied link, a failed request, a long export:
 * those do.
 *
 * The old code toasted on nearly every interaction (including "Reverted
 * changes" on a screen whose entire content had just visibly reverted), which
 * trains people to ignore the one toast that mattered.
 */

/** Turn any thrown value into something worth showing a person. */
export function errorMessage(error: unknown, fallback = 'Something went wrong'): string {
  if (error instanceof ApiError) {
    if (error.status === 0) return 'Could not reach the server. Check your connection and try again.';
    /*
     * There is no session to expire — this app has no sign-in. A 401 now means
     * the platform refused the bearer we forwarded for that read, which is a
     * data-access problem and has to read like one: telling someone to sign in
     * again would point them at a screen that does not exist. The server's own
     * reason still wins when it gave one.
     */
    if (error.status === 401)
      return error.message || 'The data platform refused this request. Check the analytics service credentials.';
    if (error.status === 403) return 'You do not have access to that.';
    if (error.status === 404) return 'That item no longer exists.';
    if (error.status === 409) return error.message || 'That conflicts with something that already exists.';
    if (error.status === 422) return error.message || 'Some details are missing or invalid.';
    if (error.status === 502) return 'The upstream data source could not be reached.';
    if (error.status >= 500) return 'The server had a problem. Please try again shortly.';
    return error.message || fallback;
  }
  if (error instanceof Error && error.message) return error.message;
  return fallback;
}

export interface NotifyOptions {
  description?: string;
  /** A single recovery action — "Retry", "Undo", "Open settings". */
  action?: { label: string; onClick: () => void };
  duration?: number;
  id?: string | number;
}

export const notify = {
  /** Confirm an outcome the user cannot see on screen. */
  success(message: string, options?: NotifyOptions) {
    return toast.success(message, options);
  },

  /**
   * Report a failure in the user's terms, and offer the way out.
   * Never pass a raw exception here — pass it through `errorMessage`.
   */
  error(message: string, options?: NotifyOptions) {
    return toast.error(message, { duration: 8000, ...options });
  },

  /** Report a caught exception. Returns the message shown, for logging. */
  failure(error: unknown, context: string, options?: NotifyOptions) {
    const detail = errorMessage(error);
    toast.error(context, { duration: 8000, description: detail, ...options });
    return detail;
  },

  info(message: string, options?: NotifyOptions) {
    return toast(message, options);
  },

  warning(message: string, options?: NotifyOptions) {
    return toast.warning(message, options);
  },

  /** Progress for an operation with no on-screen surface of its own. */
  progress<T>(
    promise: Promise<T>,
    messages: { loading: string; success: string | ((value: T) => string); error: string },
  ) {
    return toast.promise(promise, {
      loading: messages.loading,
      success: messages.success,
      error: (err: unknown) => `${messages.error}: ${errorMessage(err)}`,
    });
  },

  dismiss(id?: string | number) {
    toast.dismiss(id);
  },
};
